import { createContext, useCallback, useContext, useState, PropsWithChildren } from 'react';

export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastContextProps {
  toasts: Toast[];
  addToast: (message: string, variant?: ToastVariant | undefined) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextProps>(undefined!);

export const ToastProvider = ({ children }: PropsWithChildren) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, variant: ToastVariant = 'success') => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, message, variant }]);
      setTimeout(() => removeToast(id), 4000);
    },
    [removeToast]
  );

  return <ToastContext.Provider value={{ toasts, addToast, removeToast }}>{children}</ToastContext.Provider>;
};

export function useToast(): ToastContextProps {
  const context = useContext(ToastContext);

  if (typeof context === 'undefined') {
    throw new Error('useToast should be used within the ToastProvider!');
  }

  return context;
}
